import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import Layout from '@/components/Layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { queryClient } from '@/lib/queryClient';
import { subscribeRealtime } from '@/hooks/useSSE';

// Auth pages are the true entry point — keep them in the initial chunk.
import Login from '@/pages/Login';
import Register from '@/pages/Register';

// Everything behind auth is lazy-loaded: this keeps recharts, framer-motion and
// the table lib out of the first-paint bundle, so login/dashboard render fast
// and heavier routes stream in on demand.
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Leads = lazy(() => import('@/pages/Leads'));
const MyLeads = lazy(() => import('@/pages/MyLeads'));
const LeadDetail = lazy(() => import('@/pages/LeadDetail'));
const Companies = lazy(() => import('@/pages/Companies'));
const Contacts = lazy(() => import('@/pages/Contacts'));
const Settings = lazy(() => import('@/pages/Settings'));
const Hackathons = lazy(() => import('@/pages/Hackathons'));
const HackathonDetail = lazy(() => import('@/pages/HackathonDetail'));
const JobFairs = lazy(() => import('@/pages/JobFairs'));
const TpoDirectory = lazy(() => import('@/pages/TpoDirectory'));
const Colleges = lazy(() => import('@/pages/Colleges'));
const ChroDirectory = lazy(() => import('@/pages/ChroDirectory'));
const Candidates = lazy(() => import('@/pages/Candidates'));
const CampusIntel = lazy(() => import('@/pages/CampusIntel'));
const Intelligence = lazy(() => import('@/pages/Intelligence'));
const Armies = lazy(() => import('@/pages/Armies'));
const Team = lazy(() => import('@/pages/Team'));
// The five UptoSkills corporate lead-generation campaigns (see Sidebar's
// Campaigns section). Member-visible: agents both record and claim these leads.
const AiHiring = lazy(() => import('@/pages/corporate/AiHiring'));
const CampusDrives = lazy(() => import('@/pages/corporate/CampusDrives'));
const EmployerBranding = lazy(() => import('@/pages/corporate/EmployerBranding'));
const SkillLeague = lazy(() => import('@/pages/corporate/SkillLeague'));
const HackathonSponsorship = lazy(() => import('@/pages/corporate/HackathonSponsorship'));

function RouteFallback() {
  return (
    <div className="flex h-[60vh] items-center justify-center" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/70" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

// Fault isolation per route: a crashing chart/table on one page must not
// blank the whole outlet (and must not take the sidebar with it — the Layout
// sits ABOVE these boundaries). Keyed by pathname so navigating away from a
// broken route clears the error instead of carrying it along.
function RouteScope({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

const R = (el: React.ReactNode) => <RouteScope>{el}</RouteScope>;

function App() {
  const { isAuthenticated, user, init, ready } = useAuthStore();

  React.useEffect(() => {
    void init();
  }, []);

  // Reconnect sweep: when the realtime stream reports 'reconnected' or the
  // browser comes back online, stale queries refetch once. Pages keep their
  // own subscriptions; this only resynchronizes the shared cache.
  React.useEffect(() => {
    const resync = () => {
      queryClient.invalidateQueries().catch(() => {});
    };
    const unsub = subscribeRealtime((e) => {
      if (e?.type === 'reconnected') resync();
    });
    const onOnline = () => {
      resync();
      void import('@/lib/offlineStorage').then(({ replayOfflineActions }) => {
        void import('@/lib/api').then(({ default: api }) => {
          replayOfflineActions(api).catch(() => {});
        });
      });
    };
    window.addEventListener('online', onOnline);
    return () => {
      unsub();
      window.removeEventListener('online', onOnline);
    };
  }, []);

  // Nothing durable is stored client-side any more, so "am I signed in?" can only
  // be answered by the server via the refresh cookie. Gate the router on that
  // probe finishing; without it a reload renders the login page for a moment even
  // though the session is still valid.
  if (!ready) {
    return <RouteFallback />;
  }

  if (!isAuthenticated) {
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  const isAdmin = user?.role === 'admin';

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={R(<Dashboard />)} />
            <Route path="leads" element={R(<Leads />)} />
            <Route path="my-leads" element={R(<MyLeads />)} />
            <Route path="leads/:id" element={R(<LeadDetail />)} />
            {/* Intelligence domains keep their own routes: never one generic table. */}
            <Route path="hackathons" element={R(<Hackathons />)} />
            <Route path="hackathons/:id" element={R(<HackathonDetail />)} />
            <Route path="job-fairs" element={R(<JobFairs />)} />
            <Route path="tpo-directory" element={R(<TpoDirectory />)} />
            <Route path="colleges" element={R(<Colleges />)} />
            <Route path="college-data" element={R(<Colleges />)} />
            <Route path="college-data-page" element={R(<Colleges />)} />
            <Route path="chro-directory" element={R(<ChroDirectory />)} />
            <Route path="candidates" element={R(<Candidates />)} />
            <Route path="campus-intel" element={R(<CampusIntel />)} />
            <Route path="intelligence" element={isAdmin ? R(<Intelligence />) : <Navigate to="/dashboard" replace />} />
            <Route path="armies" element={isAdmin ? R(<Armies />) : <Navigate to="/dashboard" replace />} />
            <Route path="companies" element={R(<Companies />)} />
            <Route path="contacts" element={R(<Contacts />)} />
            <Route path="settings" element={isAdmin ? R(<Settings />) : <Navigate to="/dashboard" replace />} />
            <Route path="team" element={isAdmin ? R(<Team />) : <Navigate to="/dashboard" replace />} />
            {/* UptoSkills corporate campaigns: one route per campaign table. */}
            <Route path="ai-hiring" element={R(<AiHiring />)} />
            <Route path="campus-drives" element={R(<CampusDrives />)} />
            <Route path="employer-branding" element={R(<EmployerBranding />)} />
            <Route path="skill-league" element={R(<SkillLeague />)} />
            <Route path="hackathon-sponsorship" element={R(<HackathonSponsorship />)} />
          </Route>
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          <Route path="/register" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
